/* eslint-disable react/no-unescaped-entities */
/* eslint-disable @next/next/no-img-element */
// ─────────────────────────────────────────────────────────────
// BuddiesHR — Case study template (/case-studies/*)
// Built on the "bh" design system. Every section is data-driven:
// pass a customer object from components/caseStudies/data.
// The page frame (Header / FooterCTA / Footer) comes from the
// shared components, like the other migrated pages.
// ─────────────────────────────────────────────────────────────
import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { APPS } from '@buddieshr/components/brandLogos';
import { PRODUCTS } from '@buddieshr/utils/constants-products';

// Product page per app id. SimplePerf has no PRODUCTS entry, it lives on its
// own page. Everything else follows the shared `urlSlug || slug` rule.
const APP_ROUTES = PRODUCTS.reduce(
  (acc, p) => {
    acc[p.slug] = `/${p.urlSlug || p.slug}`;
    return acc;
  },
  { perf: '/performance-reviews' }
);

// ─── small parts ───
export function CsEyebrow({ children, color = 'var(--bh-sun-600)' }) {
  return (
    <div
      style={{
        fontSize: 12,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '.15em',
        color,
      }}
    >
      {children}
    </div>
  );
}

// Avatar: the real photo, falling back to an initials monogram if it 404s.
export function CsAvatar({ person, size = 44, ink = false }) {
  const [ok, setOk] = React.useState(Boolean(person.photo));
  const base = {
    width: size,
    height: size,
    borderRadius: '50%',
    flex: 'none',
    display: 'block',
    objectFit: 'cover',
    background: 'var(--bh-cream-deep)',
  };
  if (ok)
    return (
      <img
        src={person.photo}
        alt={person.name}
        onError={() => setOk(false)}
        style={base}
      />
    );
  return (
    <div
      aria-hidden="true"
      style={{
        ...base,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: ink ? 'rgba(255,184,77,.22)' : 'var(--bh-sun-100)',
        color: ink ? 'var(--bh-sun-300)' : 'var(--bh-sun-600)',
        border: `1.5px solid ${ink ? 'rgba(255,184,77,.35)' : 'var(--bh-sun-200)'}`,
        fontFamily: 'var(--bh-font-display)',
        fontWeight: 600,
        letterSpacing: '-0.01em',
        fontSize: Math.round(size * 0.38),
      }}
    >
      {person.initials || person.name.slice(0, 1)}
    </div>
  );
}

// Customer logo: the real file, falling back to a display-type wordmark.
export function CsLogo({ d, height = 40 }) {
  const [ok, setOk] = React.useState(Boolean(d.logo));
  if (ok)
    return (
      <img
        src={d.logo}
        alt={d.company}
        onError={() => setOk(false)}
        style={{ height, width: 'auto', display: 'block' }}
      />
    );
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height,
        fontFamily: 'var(--bh-font-display)',
        fontWeight: 600,
        fontSize: Math.round(height * 0.72),
        letterSpacing: '-0.035em',
        color: 'var(--bh-ink)',
      }}
    >
      {d.company}
    </span>
  );
}

export function CsLinkedIn({ href, label, ink = false }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 13,
        fontWeight: 600,
        color: ink ? 'var(--bh-cream)' : 'var(--bh-ink-soft)',
        textDecoration: 'none',
      }}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5ZM2.5 9.5h5V21h-5V9.5Zm7 0h4.8v1.6c.67-1.06 2-1.9 3.85-1.9 3 0 4.35 1.9 4.35 5.3V21h-5v-5.7c0-1.5-.55-2.5-1.9-2.5-1.15 0-1.85.77-2.15 1.5-.1.27-.13.65-.13 1.03V21h-5s.07-10.4 0-11.5Z" />
      </svg>
      {label}
    </a>
  );
}

// ═══ HERO ═══
export function CsHero({ d }) {
  return (
    <section
      className="bh-section-hero"
      data-screen-label="Case study hero"
      style={{ position: 'relative', overflow: 'hidden', paddingTop: 44, paddingBottom: 56 }}
    >
      <div
        className="bh-blob"
        style={{
          position: 'absolute',
          top: -300,
          right: -180,
          width: 760,
          height: 700,
          zIndex: 0,
          background:
            'radial-gradient(ellipse at 45% 45%, var(--bh-sun-100), var(--bh-sun-50) 58%, transparent 74%)',
        }}
      />
      <div
        className="cs-hero"
        style={{
          position: 'relative',
          zIndex: 1,
          maxWidth: 1160,
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: '1.35fr .9fr',
          gap: 56,
          alignItems: 'start',
        }}
      >
        <div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap',
              marginBottom: 22,
            }}
          >
            <span className="bh-chip">Case study</span>
            <span style={{ fontSize: 13.5, color: 'var(--bh-ink-mute)' }}>
              {d.industry} · {d.readTime}
            </span>
          </div>
          <h1
            style={{
              fontSize: 'clamp(32px, 4.4vw, 56px)',
              lineHeight: 1.05,
              letterSpacing: '-0.035em',
              fontWeight: 600,
            }}
          >
            {d.title[0]}
            <br />
            <span style={{ fontStyle: 'italic', color: 'var(--bh-coral-600)' }}>{d.title[1]}</span>
          </h1>
          <p
            style={{
              fontSize: 18.5,
              lineHeight: 1.55,
              color: 'var(--bh-ink-soft)',
              marginTop: 20,
              maxWidth: 560,
              textWrap: 'pretty',
            }}
          >
            {d.dek}
          </p>
          <div
            style={{
              marginTop: 26,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexWrap: 'wrap',
            }}
          >
            <a className="bh-btn primary" href="#challenge">
              Read the story ↓
            </a>
            <a className="bh-btn ghost" href="#interview">
              Jump to the interview
            </a>
          </div>
          <div style={{ marginTop: 30, display: 'flex', alignItems: 'center', gap: 14 }}>
            <CsAvatar person={d.person} size={72} />
            <div>
              <div style={{ fontWeight: 600, fontSize: 16 }}>{d.person.name}</div>
              <div
                style={{
                  fontSize: 13.5,
                  color: 'var(--bh-ink-mute)',
                  marginTop: 1,
                  marginBottom: 5,
                }}
              >
                {d.person.role}
              </div>
              <CsLinkedIn href={d.person.linkedin} label="LinkedIn" />
            </div>
          </div>
        </div>

        <aside
          className="cs-aside"
          style={{
            background: 'var(--bh-surface)',
            border: '1.5px solid var(--bh-line)',
            borderRadius: 'var(--bh-r-lg)',
            padding: 26,
            boxShadow: 'var(--bh-shadow-md)',
            position: 'sticky',
            top: 92,
          }}
        >
          <div style={{ marginBottom: 16 }}>
            <CsLogo d={d} height={40} />
          </div>
          <p
            style={{
              fontSize: 14.5,
              lineHeight: 1.5,
              color: 'var(--bh-ink-soft)',
              textWrap: 'pretty',
            }}
          >
            {d.what}
          </p>
          <div style={{ display: 'flex', gap: 14, marginTop: 12, flexWrap: 'wrap' }}>
            <a
              href={d.site}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: 'var(--bh-coral-600)',
                textDecoration: 'none',
              }}
            >
              {d.siteLabel} <span className="bh-arrow">→</span>
            </a>
            <CsLinkedIn href={d.linkedin} label="LinkedIn" />
          </div>
          <div style={{ height: 1, background: 'var(--bh-line)', margin: '20px 0' }} />
          <CsEyebrow>About the team</CsEyebrow>
          <div style={{ display: 'grid', gap: 14, marginTop: 14 }}>
            {d.facts.map(([big, small]) => (
              <div key={small} style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                <span
                  style={{
                    fontFamily: 'var(--bh-font-display)',
                    fontWeight: 600,
                    fontSize: 24,
                    letterSpacing: '-0.02em',
                    lineHeight: 1,
                    color: 'var(--bh-ink)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {big}
                </span>
                <span style={{ fontSize: 13.5, color: 'var(--bh-ink-soft)', lineHeight: 1.35 }}>
                  {small}
                </span>
              </div>
            ))}
          </div>
          <div style={{ height: 1, background: 'var(--bh-line)', margin: '20px 0' }} />
          <CsEyebrow>Using BuddiesHR since</CsEyebrow>
          <div style={{ fontSize: 14.5, marginTop: 6, fontWeight: 500 }}>{d.since}</div>
        </aside>
      </div>
    </section>
  );
}

// ═══ JUMP NAV ═══
// Sticks right under the site header (70px tall once the page scrolls).
export function CsJumpNav({ items }) {
  const [active, setActive] = React.useState(items[0][0]);
  React.useEffect(() => {
    const onScroll = () => {
      let cur = items[0][0];
      for (const [id] of items) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top < 160) cur = id;
      }
      setActive(cur);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [items]);
  // Smooth glide to the section instead of the browser's instant jump. The
  // landing offset stays the one each section sets with scrollMarginTop.
  // Reduced-motion readers keep the default jump.
  const onJump = (e, id) => {
    const el = document.getElementById(id);
    if (!el || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    e.preventDefault();
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.history.replaceState(null, '', `#${id}`);
  };
  return (
    <div className="cs-jump">
      <div
        className="cs-jump-inner"
        style={{
          maxWidth: 1160,
          margin: '0 auto',
          display: 'flex',
          gap: 4,
          overflowX: 'auto',
        }}
      >
        {items.map(([id, label]) => (
          <a
            key={id}
            href={`#${id}`}
            onClick={(e) => onJump(e, id)}
            style={{
              padding: '13px 14px',
              fontSize: 13.5,
              fontWeight: 600,
              textDecoration: 'none',
              whiteSpace: 'nowrap',
              color: active === id ? 'var(--bh-ink)' : 'var(--bh-ink-mute)',
              boxShadow: active === id ? 'inset 0 -2.5px 0 var(--bh-sun-400)' : 'none',
            }}
          >
            {label}
          </a>
        ))}
      </div>
    </div>
  );
}

// ═══ TAKEAWAYS ═══
export function CsTakeaways({ d }) {
  return (
    <section
      className="bh-section"
      data-screen-label="Takeaways"
      style={{ paddingTop: 64, paddingBottom: 8 }}
    >
      <div style={{ maxWidth: 1160, margin: '0 auto' }}>
        <CsEyebrow>Key takeaways</CsEyebrow>
        <div
          className="cs-take-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 14,
            marginTop: 18,
          }}
        >
          {d.takeaways.map(([big, small]) => (
            <div
              key={big}
              style={{
                background: 'var(--bh-surface)',
                border: '1.5px solid var(--bh-line)',
                borderRadius: 'var(--bh-r-md)',
                padding: '20px 20px 22px',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <span
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: '50%',
                  background: 'var(--bh-sun-100)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--bh-sun-600)',
                }}
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M3 8.5 6.3 12 13 4.5" />
                </svg>
              </span>
              <div
                style={{
                  fontFamily: 'var(--bh-font-display)',
                  fontSize: 18.5,
                  fontWeight: 600,
                  letterSpacing: '-0.015em',
                  lineHeight: 1.25,
                }}
              >
                {big}
              </div>
              {small && (
                <div style={{ fontSize: 14, color: 'var(--bh-ink-soft)', lineHeight: 1.5 }}>
                  {small}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ═══ PULL QUOTE ═══
export function CsQuote({ q, person, tone = 'light' }) {
  const ink = tone === 'ink';
  const parts = q.highlight ? q.text.split(q.highlight) : [q.text];
  return (
    <figure
      style={{
        position: 'relative',
        maxWidth: 860,
        margin: '0 auto',
        padding: '34px 38px 30px 46px',
        background: ink ? 'rgba(255,248,238,.06)' : 'var(--bh-surface)',
        border: `1.5px solid ${ink ? 'rgba(255,248,238,.18)' : 'var(--bh-line)'}`,
        borderRadius: 'var(--bh-r-lg)',
        boxShadow: ink ? 'none' : 'var(--bh-shadow-md)',
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 22,
          bottom: 22,
          width: 5,
          borderRadius: 99,
          background: 'linear-gradient(180deg, var(--bh-sun-400), var(--bh-coral-400))',
        }}
      />
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: 26,
          top: 6,
          fontFamily: 'var(--bh-font-display)',
          fontSize: 76,
          lineHeight: 1,
          color: ink ? 'rgba(255,184,77,.25)' : 'var(--bh-sun-200)',
          pointerEvents: 'none',
        }}
      >
        “
      </div>
      <blockquote
        style={{
          margin: 0,
          position: 'relative',
          fontFamily: 'var(--bh-font-display)',
          fontWeight: 500,
          fontSize: 'clamp(20px, 2.3vw, 27px)',
          lineHeight: 1.34,
          letterSpacing: '-0.02em',
          color: ink ? 'var(--bh-cream)' : 'var(--bh-ink)',
          textWrap: 'pretty',
        }}
      >
        {parts[0]}
        {q.highlight && (
          <span
            style={{
              background: ink ? 'rgba(255,159,28,.22)' : 'var(--bh-sun-100)',
              boxShadow: `0 0 0 3px ${ink ? 'rgba(255,159,28,.22)' : 'var(--bh-sun-100)'}`,
              borderRadius: 3,
            }}
          >
            {q.highlight}
          </span>
        )}
        {parts[1]}
      </blockquote>
      <figcaption style={{ marginTop: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
        <CsAvatar person={person} size={42} ink={ink} />
        <div>
          <div
            style={{
              fontWeight: 600,
              fontSize: 14.5,
              color: ink ? 'var(--bh-cream)' : 'var(--bh-ink)',
            }}
          >
            {person.name}
          </div>
          <div style={{ fontSize: 13, color: ink ? '#BDB1A0' : 'var(--bh-ink-mute)' }}>
            {person.role}
          </div>
        </div>
      </figcaption>
    </figure>
  );
}

// ═══ CHALLENGE ═══
export function CsChallenge({ d }) {
  return (
    <section
      id="challenge"
      className="bh-section"
      data-screen-label="Challenge"
      style={{ paddingTop: 76, paddingBottom: 0, scrollMarginTop: 130 }}
    >
      <div style={{ maxWidth: 860, margin: '0 auto' }}>
        <CsEyebrow color="var(--bh-coral-600)">The challenge</CsEyebrow>
        <h2
          style={{
            fontSize: 'clamp(27px, 3.4vw, 40px)',
            letterSpacing: '-0.03em',
            lineHeight: 1.08,
            marginTop: 12,
            fontWeight: 600,
          }}
        >
          {d.challenge.heading}
        </h2>
        <div
          style={{
            fontSize: 17.5,
            lineHeight: 1.62,
            color: 'var(--bh-ink-soft)',
            marginTop: 18,
            display: 'grid',
            gap: 16,
            textWrap: 'pretty',
          }}
        >
          {d.challenge.body.map((paragraph, i) => (
            <p key={i}>{paragraph}</p>
          ))}
        </div>
      </div>
      <div style={{ marginTop: 34 }}>
        <CsQuote q={d.quotes.challenge} person={d.person} />
      </div>
      <div
        style={{
          maxWidth: 860,
          margin: '0 auto',
          paddingTop: 30,
          fontSize: 17.5,
          lineHeight: 1.62,
          color: 'var(--bh-ink-soft)',
        }}
      >
        <p>{d.challenge.after}</p>
      </div>
    </section>
  );
}

// ═══ BEFORE / AFTER ═══
export function CsBeforeAfter({ d }) {
  const col = (title, items, tone) => (
    <div
      style={{
        background: tone === 'after' ? 'var(--bh-surface)' : 'transparent',
        border: `1.5px ${tone === 'after' ? 'solid var(--bh-ink)' : 'dashed var(--bh-line-strong)'}`,
        borderRadius: 'var(--bh-r-lg)',
        padding: '24px 26px 26px',
        boxShadow: tone === 'after' ? '0 4px 0 var(--bh-ink)' : 'none',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 16 }}>
        <span
          style={{
            fontSize: 11.5,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '.14em',
            color: tone === 'after' ? 'var(--bh-ink)' : 'var(--bh-ink-mute)',
          }}
        >
          {title}
        </span>
      </div>
      <div style={{ display: 'grid', gap: 11 }}>
        {items.map((t) => (
          <div key={t} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <span
              style={{
                flex: 'none',
                marginTop: 3,
                color: tone === 'after' ? 'var(--app-clappy)' : 'var(--bh-ink-mute)',
              }}
            >
              {tone === 'after' ? (
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M3 8.5 6.3 12 13 4.5" />
                </svg>
              ) : (
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                >
                  <path d="M4 4l8 8M12 4l-8 8" />
                </svg>
              )}
            </span>
            <span
              style={{
                fontSize: 15.5,
                lineHeight: 1.45,
                color: tone === 'after' ? 'var(--bh-ink)' : 'var(--bh-ink-mute)',
              }}
            >
              {t}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
  return (
    <section
      className="bh-section"
      data-screen-label="Before after"
      style={{ paddingTop: 72, paddingBottom: 0 }}
    >
      <div style={{ maxWidth: 1000, margin: '0 auto' }}>
        <div
          className="cs-ba"
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto 1fr',
            gap: 22,
            alignItems: 'center',
          }}
        >
          {col('Before', d.beforeAfter.before, 'before')}
          <div
            className="cs-ba-arrow"
            style={{
              width: 42,
              height: 42,
              borderRadius: '50%',
              background: 'var(--bh-sun-300)',
              border: '1.5px solid var(--bh-ink)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18,
              flex: 'none',
            }}
          >
            →
          </div>
          {col('With BuddiesHR', d.beforeAfter.after, 'after')}
        </div>
      </div>
    </section>
  );
}

// ═══ SOLUTION ═══
export function CsSolution({ d }) {
  const byId = Object.fromEntries(APPS.map((a) => [a.id, a]));
  return (
    <section
      id="solution"
      className="bh-section"
      data-screen-label="Solution"
      style={{ paddingTop: 76, paddingBottom: 0, scrollMarginTop: 130 }}
    >
      <div style={{ maxWidth: 1000, margin: '0 auto' }}>
        <CsEyebrow>Solution &amp; implementation</CsEyebrow>
        <h2
          style={{
            fontSize: 'clamp(27px, 3.4vw, 40px)',
            letterSpacing: '-0.03em',
            lineHeight: 1.08,
            marginTop: 12,
            fontWeight: 600,
            maxWidth: 720,
          }}
        >
          What they were looking for, in their order
        </h2>
        <div
          className="cs-sol"
          style={{
            display: 'grid',
            gridTemplateColumns: '1.1fr .9fr',
            gap: 44,
            marginTop: 30,
            alignItems: 'start',
          }}
        >
          <div style={{ display: 'grid', gap: 10 }}>
            {d.criteria.map((c, i) => (
              <div
                key={c}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  background: 'var(--bh-surface)',
                  border: '1.5px solid var(--bh-line)',
                  borderRadius: 'var(--bh-r-md)',
                  padding: '14px 18px',
                }}
              >
                <span
                  style={{
                    fontFamily: 'var(--bh-font-display)',
                    fontWeight: 600,
                    fontSize: 19,
                    color: 'var(--bh-sun-500)',
                    minWidth: 34,
                  }}
                >
                  #{i + 1}
                </span>
                <span style={{ fontSize: 16.5, fontWeight: 500 }}>{c}</span>
              </div>
            ))}
          </div>
          <div>
            {d.foundUs && (
              <div
                style={{
                  background: 'var(--bh-cream-deep)',
                  border: '1.5px solid var(--bh-line)',
                  borderRadius: 'var(--bh-r-md)',
                  padding: '18px 20px',
                }}
              >
                <CsEyebrow color="var(--bh-ink-mute)">How they found us</CsEyebrow>
                <div
                  style={{
                    fontFamily: 'var(--bh-font-display)',
                    fontSize: 22,
                    fontWeight: 600,
                    marginTop: 6,
                    letterSpacing: '-0.02em',
                  }}
                >
                  {d.foundUs}
                </div>
              </div>
            )}
            <div style={{ marginTop: d.foundUs ? 18 : 0 }}>
              <CsEyebrow color="var(--bh-ink-mute)">Why they bought</CsEyebrow>
              <ol
                style={{
                  margin: '12px 0 0',
                  padding: 0,
                  listStyle: 'none',
                  display: 'grid',
                  gap: 9,
                }}
              >
                {d.whyBuy.map((w, i) => (
                  <li
                    key={w}
                    style={{
                      display: 'flex',
                      gap: 10,
                      fontSize: 15.5,
                      color: 'var(--bh-ink-soft)',
                      lineHeight: 1.45,
                    }}
                  >
                    <span style={{ color: 'var(--bh-coral-600)', fontWeight: 700 }}>#{i + 1}</span>
                    {w}
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>

        <div style={{ marginTop: 40 }}>
          <CsEyebrow color="var(--bh-ink-mute)">Apps running in their Slack</CsEyebrow>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 14 }}>
            {d.apps.map((id) => {
              const app = byId[id];
              if (!app) return null;
              const Logo = app.Logo;
              return (
                <Link
                  key={id}
                  href={`${APP_ROUTES[id] || '/'}?ref=${d.slug}-case-study`}
                  className="cs-app-pill"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    background: 'var(--bh-surface)',
                    border: `1.5px solid ${app.color}`,
                    borderRadius: 'var(--bh-r-pill)',
                    padding: '7px 16px 7px 8px',
                    color: 'var(--bh-ink)',
                    textDecoration: 'none',
                  }}
                >
                  <span
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: 9,
                      overflow: 'hidden',
                      background: app.bg,
                      flex: 'none',
                    }}
                  >
                    <Logo />
                  </span>
                  <span style={{ fontWeight: 600, fontSize: 15 }}>{app.name}</span>
                  <span style={{ fontSize: 13, color: 'var(--bh-ink-mute)' }}>{app.tagline}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

// ═══ TEAM PHOTO ═══
// Rendered only when the customer object carries a `teamPhoto`.
export function CsTeamPhoto({ d, tone = 'light' }) {
  const photo = d.teamPhoto;
  if (!photo || !photo.src) return null;
  const ink = tone === 'ink';
  return (
    <figure
      className="cs-team"
      style={{ margin: '0 auto', maxWidth: ink ? 860 : 1160 }}
    >
      <div
        style={{
          position: 'relative',
          borderRadius: 'var(--bh-r-xl)',
          overflow: 'hidden',
          border: `1.5px solid ${ink ? 'rgba(255,248,238,.18)' : 'var(--bh-ink)'}`,
          background: ink ? 'rgba(255,248,238,.04)' : 'var(--bh-cream-deep)',
          boxShadow: ink ? 'none' : '0 5px 0 var(--bh-ink)',
          height: ink ? 400 : 460,
        }}
      >
        <Image
          src={photo.src}
          alt={photo.alt || photo.caption}
          fill
          sizes="(max-width: 1200px) 100vw, 1160px"
          style={{ objectFit: 'cover' }}
        />
      </div>
      <figcaption
        style={{
          marginTop: 12,
          fontSize: 13.5,
          color: ink ? '#BDB1A0' : 'var(--bh-ink-mute)',
        }}
      >
        {photo.caption}
      </figcaption>
    </figure>
  );
}

// Full-width team photo, sitting right under the hero.
export function CsTeamBand({ d }) {
  if (!d.teamPhoto) return null;
  return (
    <section
      className="bh-section"
      data-screen-label="Team photo"
      style={{ paddingTop: 8, paddingBottom: 4 }}
    >
      <CsTeamPhoto d={d} />
    </section>
  );
}

// ═══ RESULTS (ink) ═══
// Link styling for the prose inside the ink band — the body copy lives in
// components/caseStudies/data and uses this for its inline links.
export function CsInkLink({ href, children }) {
  return (
    <Link
      href={href}
      style={{
        color: 'var(--bh-sun-300)',
        textDecoration: 'underline',
        textDecorationColor: 'rgba(255,184,77,.4)',
        textUnderlineOffset: 3,
      }}
    >
      {children}
    </Link>
  );
}

export function CsResults({ d }) {
  return (
    <section
      id="results"
      className="bh-section"
      data-screen-label="Results"
      style={{
        marginTop: 84,
        paddingTop: 78,
        paddingBottom: 78,
        background: 'var(--bh-ink)',
        color: 'var(--bh-cream)',
        position: 'relative',
        overflow: 'hidden',
        scrollMarginTop: 116,
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(90% 120% at 88% 8%, rgba(255,159,28,.20), transparent 60%)',
          pointerEvents: 'none',
        }}
      />
      <div style={{ position: 'relative', maxWidth: 1000, margin: '0 auto' }}>
        <CsEyebrow color="var(--bh-sun-300)">Results</CsEyebrow>
        <h2
          style={{
            fontSize: 'clamp(27px, 3.4vw, 40px)',
            letterSpacing: '-0.03em',
            lineHeight: 1.08,
            marginTop: 12,
            fontWeight: 600,
            color: 'var(--bh-cream)',
          }}
        >
          {d.resultsSection.heading}
        </h2>
        <div
          className="cs-res-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 12,
            marginTop: 30,
          }}
        >
          {d.results.map((r) => (
            <div
              key={r}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 13,
                background: 'rgba(255,248,238,.05)',
                border: '1.5px solid rgba(255,248,238,.16)',
                borderRadius: 'var(--bh-r-md)',
                padding: '18px 20px',
              }}
            >
              <span style={{ flex: 'none', color: 'var(--bh-sun-300)' }}>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M4 17 10 11l3.5 3.5L20 8" />
                  <path d="M20 13V8h-5" />
                </svg>
              </span>
              <span style={{ fontSize: 16.5, fontWeight: 500, lineHeight: 1.35 }}>{r}</span>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 34 }}>
          <CsQuote q={d.quotes.result} person={d.person} tone="ink" />
        </div>
        <div
          style={{
            maxWidth: 860,
            margin: '30px auto 0',
            display: 'grid',
            gap: 14,
            fontSize: 17,
            lineHeight: 1.6,
            color: 'rgba(255,248,238,.82)',
          }}
        >
          {d.resultsSection.body.map((paragraph, i) => (
            <p key={i}>{paragraph}</p>
          ))}
        </div>
      </div>
    </section>
  );
}

// ═══ CLOSING ═══
export function CsClosing({ d }) {
  return (
    <section
      className="bh-section"
      data-screen-label="Final thoughts"
      style={{ paddingTop: 80, paddingBottom: 0 }}
    >
      <div style={{ maxWidth: 860, margin: '0 auto' }}>
        <CsEyebrow>Final thoughts</CsEyebrow>
        <div
          style={{
            marginTop: 16,
            display: 'grid',
            gap: 16,
            fontSize: 17.5,
            lineHeight: 1.62,
            color: 'var(--bh-ink-soft)',
            textWrap: 'pretty',
          }}
        >
          {d.closing.map((p) => (
            <p key={p}>{p}</p>
          ))}
        </div>
      </div>
    </section>
  );
}

// ═══ FULL INTERVIEW (collapsed) ═══
export function CsInterview({ d }) {
  return (
    <section
      id="interview"
      className="bh-section"
      data-screen-label="Interview"
      style={{ paddingTop: 80, paddingBottom: 0, scrollMarginTop: 130 }}
    >
      <div style={{ maxWidth: 860, margin: '0 auto' }}>
        <CsEyebrow>The full interview</CsEyebrow>
        <p
          style={{
            fontSize: 16,
            color: 'var(--bh-ink-soft)',
            marginTop: 12,
            lineHeight: 1.55,
          }}
        >
          Every question we asked {d.person.name}, answered in their own words. Nothing edited.
        </p>
        <div style={{ marginTop: 22, display: 'grid', gap: 26 }}>
          {d.interview.map(([group, qs]) => (
            <div key={group}>
              <h3
                style={{
                  fontFamily: 'var(--bh-font-display)',
                  fontSize: 19,
                  fontWeight: 600,
                  letterSpacing: '-0.02em',
                  marginBottom: 10,
                }}
              >
                {group}
              </h3>
              <div style={{ display: 'grid', gap: 8 }}>
                {qs.map(([q, a]) => (
                  <details key={q} className="cs-qa">
                    <summary>
                      <span style={{ fontSize: 15.5, fontWeight: 600, lineHeight: 1.4 }}>{q}</span>
                      <span className="cs-qa-chev" aria-hidden="true">
                        <svg
                          width="15"
                          height="15"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M6 9l6 6 6-6" />
                        </svg>
                      </span>
                    </summary>
                    <div
                      style={{
                        padding: '18px 22px 20px',
                        display: 'grid',
                        gap: 12,
                        fontSize: 15.5,
                        lineHeight: 1.6,
                        color: 'var(--bh-ink-soft)',
                      }}
                    >
                      {a.split('\n\n').map((para) => (
                        <p key={para} style={{ textWrap: 'pretty' }}>
                          {para}
                        </p>
                      ))}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ═══ MORE / CTA ═══
// The design linked a "/case-studies" index that doesn't exist on the site,
// so this points at the other customer story and the reviews wall instead.
export function CsMore({ other }) {
  return (
    <section
      className="bh-section"
      data-screen-label="More"
      style={{ paddingTop: 80, paddingBottom: 72 }}
    >
      <div
        style={{
          maxWidth: 1000,
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 24,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h2
            style={{
              fontSize: 'clamp(22px, 2.6vw, 30px)',
              letterSpacing: '-0.025em',
              fontWeight: 600,
            }}
          >
            More from our customers
          </h2>
          <p style={{ fontSize: 15.5, color: 'var(--bh-ink-soft)', marginTop: 8 }}>
            1,000+ teams run their culture on BuddiesHR.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {other && (
            <Link className="bh-btn ghost" href={other.href}>
              {other.label}
            </Link>
          )}
          <Link className="bh-btn ghost" href="/reviews">
            Read the reviews <span className="bh-arrow">→</span>
          </Link>
        </div>
      </div>
    </section>
  );
}
